import { useState, useEffect } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  List,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import ValidationItem from "../components/ValidationItem";

export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false); // Toggle state
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [checks, setChecks] = useState({
    length: false,
    number: false,
    special: false,
    match: false,
  });

  // Check for valid session on mount
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("error=")) {
      const params = new URLSearchParams(hash.replace("#", "?"));
      const errorMsg = params.get("error_description") || "Invalid link";
      setMsg({
        type: "error",
        text: `Link Error: ${errorMsg.replace(/\+/g, " ")}`,
      });
      return;
    }
    const checkSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      // If there is no session, it means they didn't come from a valid email link
      if (!session) {
        setMsg({
          type: "error",
          text: "This link is invalid or has expired. Please request a new password reset.",
        });
        // Optionally redirect them away after a few seconds
        setTimeout(() => navigate("/login"), 5000);
      }
    };

    checkSession();
  }, [navigate]);

  useEffect(() => {
    setChecks({
      length: password.length >= 8,
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*]/.test(password),
      match: password !== "" && password === confirmPassword,
    });
  }, [password, confirmPassword]);

  const isFormValid = Object.values(checks).every(Boolean);

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const handleReset = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        setMsg({ type: "error", text: error.message });
      } else {
        setMsg({ type: "success", text: "Password updated! Redirecting..." });
        setTimeout(() => navigate("/login"), 3000);
      }
    } catch (err) {
      setMsg({ type: "error", text: "Unexpected error occurred." });
    } finally {
      setLoading(false);
    }
  };

  // -------------------- Render -------------------- //
  return (
    <Container maxWidth="xs" sx={{ py: 8 }}>
      <Typography variant="h4" gutterBottom>
        Set New Password
      </Typography>

      {msg.text && (
        <Alert severity={msg.type} sx={{ mb: 2 }}>
          {msg.text}
        </Alert>
      )}

      <Box component="form" onSubmit={handleReset} noValidate>
        <TextField
          label="New Password"
          type={showPassword ? "text" : "password"}
          fullWidth
          margin="normal"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClickShowPassword}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        <List sx={{ mb: 1 }}>
          <ValidationItem
            fulfilled={checks.length}
            text="At least 8 characters"
          />
          <ValidationItem fulfilled={checks.number} text="Contains a number" />
          <ValidationItem
            fulfilled={checks.special}
            text="Contains a special character (!@#$)"
          />
        </List>

        <TextField
          label="Confirm New Password"
          type={showPassword ? "text" : "password"}
          fullWidth
          margin="normal"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          onPaste={(e) => e.preventDefault()} // Optional: Prevents paste in confirmation
          required
        />
        <ValidationItem fulfilled={checks.match} text="Passwords match" />

        <Button
          variant="contained"
          fullWidth
          sx={{ mt: 3 }}
          type="submit"
          disabled={loading || !isFormValid}
        >
          {loading ? "Updating..." : "Update Password"}
        </Button>
      </Box>
    </Container>
  );
}
